import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomSelect, { type SelectOption } from '@/components/ui/CustomSelect';
import styles from './styles.module.scss';

export type ParticipantStatsFilterValue =
  | 'all'
  | 'win'
  | 'loss'
  | 'exactScore'
  | 'outcomeResult'
  | 'pending';

export interface ParticipantStatsFilterProps {
  value: ParticipantStatsFilterValue;
  onChange: (next: ParticipantStatsFilterValue) => void;
}

/**
 * Toolbar-style filter for the participant statistics page — mirrors the
 * `ParticipantsTable` toolbar layout (a row of select wrappers) so the two
 * tabs feel visually consistent. Single dropdown holding the 5 outcome
 * categories that previously lived as chip buttons.
 */
export const ParticipantStatsFilter: React.FC<ParticipantStatsFilterProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation(['tournament']);

  const options = useMemo<SelectOption[]>(() => [
    { value: 'all',            label: t('tournament:participantStats.filter.all') },
    { value: 'win',            label: t('tournament:participantStats.filter.win') },
    { value: 'loss',           label: t('tournament:participantStats.filter.loss') },
    { value: 'exactScore',     label: t('tournament:participantStats.filter.exactScore') },
    { value: 'outcomeResult',  label: t('tournament:participantStats.filter.outcomeResult') },
    { value: 'pending',        label: t('tournament:participantStats.filter.pending') },
  ], [t]);

  const current = useMemo(
    () => options.find(o => o.value === value) || options[0],
    [options, value],
  );

  return (
    <div className={styles.toolbar}>
      <div className={styles.filterSelectWrapper}>
        <CustomSelect
          options={options}
          value={current}
          onChange={(val) => {
            if (val) onChange(val.value as ParticipantStatsFilterValue);
          }}
          variant="basic"
          isSearchable={false}
          menuPortal
        />
      </div>
    </div>
  );
};

export default ParticipantStatsFilter;
